"use client";

import { ReactNodeViewRenderer } from "@tiptap/react";
import { mergeAttributes, Range } from "@tiptap/core";

import { ImageBlockView } from "./components/ImageBlockView.js";
import { Image } from "../Image/Image.js";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    imageBlock: {
      setImageBlock: (attributes: {
        src: string;
        assetId?: string;
        caption?: string;
        actualWidth?: number;
        actualHeight?: number;
      }) => ReturnType;
      setImageBlockAt: (attributes: {
        src: string;
        pos: number | Range;
        assetId?: string;
        caption?: string;
        actualWidth?: number;
        actualHeight?: number;
      }) => ReturnType;
      setImageBlockAlign: (align: "left" | "center" | "right") => ReturnType;
      setImageBlockWidth: (width: number) => ReturnType;
      setImageCaption: (caption: string) => ReturnType;
    };
  }
}

export const ImageBlock = Image.extend({
  name: "imageBlock",

  group: "block",

  defining: true,

  isolating: true,

  addAttributes() {
    return {
      src: {
        default: "",
        parseHTML: (element) => element.getAttribute("src"),
        renderHTML: (attributes) => ({
          src: attributes.src,
        }),
      },
      width: {
        default: "100%",
        parseHTML: (element) => element.getAttribute("data-width"),
        renderHTML: (attributes) => ({
          "data-width": attributes.width,
        }),
      },
      actualWidth: {
        default: 0,
        parseHTML: (element) => element.getAttribute("data-actual-width"),
        renderHTML: (attributes) => ({
          "data-actual-width": attributes.actualWidth,
        }),
      },
      actualHeight: {
        default: 0,
        parseHTML: (element) => element.getAttribute("data-actual-height"),
        renderHTML: (attributes) => ({
          "data-actual-height": attributes.actualHeight,
        }),
      },
      align: {
        default: "center",
        parseHTML: (element) => element.getAttribute("data-align"),
        renderHTML: (attributes) => ({
          "data-align": attributes.align,
        }),
      },
      alt: {
        default: "",
        parseHTML: (element) => element.getAttribute("alt"),
        renderHTML: (attributes) => ({
          alt: attributes.alt,
        }),
      },
      caption: {
        default: "",
        parseHTML: (element) => element.getAttribute("caption"),
        renderHTML: (attributes) => ({
          caption: attributes.caption,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[src*="tiptap.dev"]:not([src^="data:"]), img[src*="windows.net"]:not([src^="data:"])',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      { class: "imageBlock" },
      ["img", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)],
      [
        "textarea",
        mergeAttributes(
          {
            class:
              "w-full  text-gray-400 h-16 mb-2 placeholder-black p-1 text-sm  text-gray-400 border-0 rounded appearance-none",
          },
          { defaultValue: HTMLAttributes.caption }
        ),
        HTMLAttributes.caption,
      ],
    ];
  },

  addCommands() {
    return {
      setImageBlock:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: "imageBlock",
            attrs: {
              src: attrs.src,
              caption: attrs.caption,
              actualWidth: attrs.actualWidth,
              actualHeight: attrs.actualHeight,
            },
          });
        },

      setImageBlockAt:
        (attrs) =>
        ({ commands }) => {
          console.log(attrs);
          return commands.insertContentAt(attrs.pos, {
            type: "imageBlock",
            attrs: {
              src: attrs.src,
              caption: attrs.caption,
              actualWidth: attrs.actualWidth,
              actualHeight: attrs.actualHeight,
            },
          });
        },
      setImageCaption:
        (caption) =>
        ({ commands }) =>
          commands.updateAttributes("imageBlock", { caption }),
      setImageBlockAlign:
        (align) =>
        ({ commands }) =>
          commands.updateAttributes("imageBlock", { align }),

      setImageBlockWidth:
        (width) =>
        ({ commands }) =>
          commands.updateAttributes("imageBlock", {
            width: `${Math.max(0, Math.min(100, width))}%`,
          }),
    };
  },

  addNodeView() {
    //@ts-ignore
    return ReactNodeViewRenderer(ImageBlockView);
  },
});

export default ImageBlock;
